// AutokeyPattern.js — designable autokey templates for node types
//
// Tokens:
//   {{type}}     first 4 chars of type key (A–Z0–9, uppercase)
//   {{year}}     4-digit calendar year at assign time
//   {{seq}}      sequence number
//   {{seq:N}}    sequence zero-padded to N digits (1–12)
//
// Empty / missing pattern → runtime keeps legacy PREFIX-N behavior.

const TOKEN_RE = /\{\{\s*(type|year|seq)(?:\s*:\s*(\d+))?\s*\}\}/gi;

export const DEFAULT_AUTOKEY_PATTERN = '{{type}}-{{year}}-{{seq:4}}';
export const DEFAULT_AUTOKEY_PATTERN_PLACEHOLDER = DEFAULT_AUTOKEY_PATTERN;

export function normalizeAutokeyPattern(raw) {
  if (typeof raw !== 'string') return '';
  return raw.trim();
}

export function buildTypeToken(typeKey) {
  const normalized = String(typeKey || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 4);
  return normalized || 'NODE';
}

function clampSeqPad(rawPad) {
  const pad = Number(rawPad);
  if (!Number.isInteger(pad) || pad < 1) return null;
  return Math.min(pad, 12);
}

/**
 * Parse an autokey pattern into literal / token parts.
 */
export function parseAutokeyPattern(pattern) {
  const normalized = normalizeAutokeyPattern(pattern);
  if (!normalized) {
    return {
      valid: false,
      error: 'empty',
      usesYear: false,
      tokens: [],
    };
  }

  const tokens = [];
  let cursor = 0;
  let usesYear = false;
  let hasSeq = false;
  let match;

  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(normalized)) !== null) {
    if (match.index > cursor) {
      tokens.push({
        kind: 'literal',
        value: normalized.slice(cursor, match.index),
      });
    }

    const name = String(match[1] || '').toLowerCase();
    if (name === 'type') {
      tokens.push({ kind: 'type' });
    } else if (name === 'year') {
      usesYear = true;
      tokens.push({ kind: 'year' });
    } else if (name === 'seq') {
      hasSeq = true;
      tokens.push({ kind: 'seq', pad: clampSeqPad(match[2]) });
    }

    cursor = match.index + match[0].length;
  }

  if (cursor < normalized.length) {
    tokens.push({
      kind: 'literal',
      value: normalized.slice(cursor),
    });
  }

  if (!hasSeq) {
    return {
      valid: false,
      error: 'Pattern must include {{seq}} or {{seq:N}}.',
      usesYear,
      tokens,
    };
  }

  if (!tokens.length) {
    return {
      valid: false,
      error: 'empty',
      usesYear: false,
      tokens: [],
    };
  }

  return {
    valid: true,
    usesYear,
    tokens,
  };
}

function formatSeqValue(seq, pad) {
  const n = Number(seq);
  const safe = Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  if (pad == null) return String(safe);
  return String(safe).padStart(pad, '0');
}

/**
 * Render an autokey from a pattern + values.
 */
export function formatAutokeyFromPattern(pattern, values = {}) {
  const parsed = parseAutokeyPattern(pattern);
  if (!parsed.valid) return '';

  const typeToken = buildTypeToken(values.typeKey);
  const year = String(values.year ?? new Date().getUTCFullYear());
  const seq = values.seq;

  return parsed.tokens.map((token) => {
    if (token.kind === 'literal') return token.value;
    if (token.kind === 'type') return typeToken;
    if (token.kind === 'year') return year;
    if (token.kind === 'seq') return formatSeqValue(seq, token.pad);
    return '';
  }).join('');
}

/**
 * Preview helper for admin UI.
 */
export function previewAutokeyPattern(pattern, typeKey, {
  year = new Date().getUTCFullYear(),
  seq = 142,
} = {}) {
  const normalized = normalizeAutokeyPattern(pattern);
  if (!normalized) {
    const typeToken = buildTypeToken(typeKey);
    return `${typeToken}-${seq}`;
  }
  const parsed = parseAutokeyPattern(normalized);
  if (!parsed.valid) return '';
  return formatAutokeyFromPattern(normalized, { typeKey, year, seq });
}
